// The common operating picture, narrowed to exactly what an S-2 estimate is allowed to reason from.
//
// This module exists to make one rule structural rather than a promise. The estimator — heuristic or
// Jev — must never see ground truth: not an enemy unit's real position, not its real type, not its
// real strength. `Snapshot` already filters enemies out for the live stage, but the headless
// calibration harness runs the simulation directly and holds every red unit in its hand, so a context
// builder that merely "does not read" those fields would be one careless line away from leaking them
// into a prompt and inventing an S-2 that is never wrong.
//
// So `EstimatorContext` has no field that could hold one. The only enemy information in it is a
// `CopContact`, which is a *report*: an errored position, a degraded type guess, and the two ticks that
// say how old it is. `estimatorContextFromSnapshot` is the single narrowing point, and
// `tests/ai/estimator.test.ts` builds a snapshot whose red unit sits a kilometre from its reported
// contact and asserts that the true position appears nowhere in the state that goes over the wire.
//
// The corridor frame below is the same geometry the scripted red cell reasons with (`src/sim/redcell.ts`):
// whichever coordinate varies along a phase line is the width axis, thirds are grouped across it, and
// the other axis is depth. Deriving it here from the control graphics — rather than importing RedCell,
// which would drag the whole simulation into a module the browser loads for one card — keeps the two
// definitions independently checkable, which matters because the calibration labels are read off red's
// behaviour and the estimate is read off blue's picture.

import type { Contact, Dials, Graphic, Scenario, Snapshot, Unit, UnitType } from '../sim/types';
import { TICKS_PER_MIN } from '../sim/types';

// ---- corridor geometry -------------------------------------------------------------------------------

export type Third = 0 | 1 | 2;

/** The five names a third can carry. Which three are in play depends on how the corridor runs, which is
 * why this is a five-way union and not a three-way one: a north-south corridor is thirded
 * south/center/north, an east-west one west/center/east. */
export type ThirdName = 'north' | 'center' | 'south' | 'west' | 'east';

export const ALL_THIRD_NAMES: readonly ThirdName[] = ['north', 'center', 'south', 'west', 'east'] as const;

export interface CorridorFrame {
  depthAxis: 'x' | 'y';
  widthAxis: 'x' | 'y';
  widthMin: number;
  widthMax: number;
  /** In index order 0,1,2 across the width axis. */
  names: readonly [ThirdName, ThirdName, ThirdName];
  /** +1 when the objective lies at increasing depth, -1 when it lies at decreasing depth. */
  depthSign: 1 | -1;
  /** Phase lines and the objective in advance order, with the depth coordinate of each. */
  bands: { id: string; name: string; depth: number; kind: 'pl' | 'obj' }[];
}

function pointAxis(p: { x: number; y: number }, axis: 'x' | 'y'): number {
  return axis === 'x' ? p.x : p.y;
}

function thirdOfRange(w: number, widthMin: number, widthMax: number): Third {
  const span = Math.max(1, widthMax - widthMin);
  const frac = Math.min(1, Math.max(0, (w - widthMin) / span));
  return Math.min(2, Math.floor(frac * 3)) as Third;
}

/**
 * Derives the corridor frame from the control graphics.
 *
 * `extraPoints` widens the width extent when the phase lines are shorter than the force is wide, which
 * happens on a hand-drawn graphic; without it a flank unit can land outside [widthMin, widthMax] and be
 * clamped into the nearest third, which is the right behaviour but a worse estimate.
 */
export function corridorFrame(
  graphics: readonly Graphic[],
  objectiveId?: string,
  extraPoints: readonly { x: number; y: number }[] = [],
): CorridorFrame {
  const pls = graphics.filter((g) => g.kind === 'pl');
  const plRed = pls.find((g) => g.name.toUpperCase().includes('RED')) ?? pls[0];
  const plWhite = pls.find((g) => g.name.toUpperCase().includes('WHITE')) ?? pls[1] ?? plRed;
  const plBlue = pls.find((g) => g.name.toUpperCase().includes('BLUE')) ?? pls[2];
  const obj = graphics.find((g) => g.id === objectiveId) ?? graphics.find((g) => g.kind === 'obj');

  const refLine = plWhite ?? plRed ?? plBlue;
  let depthAxis: 'x' | 'y' = 'x';
  let widthAxis: 'x' | 'y' = 'y';
  let widthMin = 0;
  let widthMax = 1;

  if (refLine && refLine.pts.length >= 2) {
    const [p1, p2] = refLine.pts;
    const dx = Math.abs(p2.x - p1.x);
    const dy = Math.abs(p2.y - p1.y);
    if (dy > dx) { depthAxis = 'x'; widthAxis = 'y'; } else { depthAxis = 'y'; widthAxis = 'x'; }
    widthMin = Math.min(pointAxis(p1, widthAxis), pointAxis(p2, widthAxis));
    widthMax = Math.max(pointAxis(p1, widthAxis), pointAxis(p2, widthAxis));
  }

  for (const p of extraPoints) {
    const w = pointAxis(p, widthAxis);
    if (w < widthMin) widthMin = w;
    if (w > widthMax) widthMax = w;
  }

  // Row 0 is the AO's south edge and column 0 its west edge (see `Grid`), so a corridor thirded across
  // y reads south/center/north and one thirded across x reads west/center/east.
  const names: readonly [ThirdName, ThirdName, ThirdName] = widthAxis === 'y'
    ? ['south', 'center', 'north']
    : ['west', 'center', 'east'];

  const depthOf = (p: { x: number; y: number }): number => pointAxis(p, depthAxis);
  const whiteDepth = plWhite && plWhite.pts.length > 0 ? depthOf(plWhite.pts[0]) : 0;
  const objDepth = obj && obj.pts.length > 0 ? depthOf(obj.pts[0]) : whiteDepth + 1;
  const depthSign: 1 | -1 = objDepth >= whiteDepth ? 1 : -1;

  const bands: CorridorFrame['bands'] = [];
  for (const g of [plRed, plWhite, plBlue]) {
    if (g && g.pts.length > 0 && !bands.some((b) => b.id === g.id)) {
      bands.push({ id: g.id, name: g.name, depth: depthOf(g.pts[0]), kind: 'pl' });
    }
  }
  if (obj && obj.pts.length > 0) bands.push({ id: obj.id, name: obj.name, depth: objDepth, kind: 'obj' });
  bands.sort((a, b) => depthSign * (a.depth - b.depth));

  return { depthAxis, widthAxis, widthMin, widthMax, names, depthSign, bands };
}

/** Which third of the corridor a point sits in. */
export function thirdOf(frame: CorridorFrame, p: { x: number; y: number }): Third {
  return thirdOfRange(pointAxis(p, frame.widthAxis), frame.widthMin, frame.widthMax);
}

/** The name of that third. */
export function thirdNameOf(frame: CorridorFrame, p: { x: number; y: number }): ThirdName {
  return frame.names[thirdOf(frame, p)];
}

/** The depth coordinate of a point, signed so that larger always means "further toward the objective". */
export function advanceDepth(frame: CorridorFrame, p: { x: number; y: number }): number {
  return frame.depthSign * pointAxis(p, frame.depthAxis);
}

/** True when `p` has passed the named band on the way to the objective. */
export function isPastBand(frame: CorridorFrame, p: { x: number; y: number }, bandId: string): boolean {
  const band = frame.bands.find((b) => b.id === bandId);
  if (!band) return false;
  return advanceDepth(frame, p) > frame.depthSign * band.depth;
}

/** A point's position in words: "before PL RED", "PL RED to PL WHITE", "beyond PL BLUE". */
export function depthBandLabel(frame: CorridorFrame, p: { x: number; y: number }): string {
  const pls = frame.bands.filter((b) => b.kind === 'pl');
  if (pls.length === 0) return 'unreferenced';
  const d = advanceDepth(frame, p);
  for (let i = 0; i < pls.length; i++) {
    if (d <= frame.depthSign * pls[i].depth) {
      return i === 0 ? `before ${pls[0].name}` : `${pls[i - 1].name} to ${pls[i].name}`;
    }
  }
  return `beyond ${pls[pls.length - 1].name}`;
}

// ---- the narrowed picture ------------------------------------------------------------------------------

/** One enemy contact as blue holds it. Every number here is a *report*, never ground truth. */
export interface CopContact {
  enemyId: number;
  /** The reported position in centimetres: the true position perturbed within `errorM` by the detection
   * model, which is what `Contact` carries and what the report feed and every prompt are built from. */
  x: number;
  y: number;
  errorM: number;
  /** What the observer could make of it: the real type only inside 1 km, 'vehicle' inside 2.5 km, else
   * 'unknown'. A wrong guess here stays wrong all the way to the estimate, which is the point. */
  typeConf: Contact['typeConf'];
  lastSeen: number;
  reportedAt: number;
  /** The tick this enemy was first ever reported. First sightings are what a role judgment hangs on. */
  firstSeen: number;
}

/** One of blue's own units. Blue legitimately knows where its own platoons are. */
export interface CopOwnUnit {
  id: number;
  callsign: string;
  type: UnitType;
  x: number;
  y: number;
  strength: number;
  alive: boolean;
  inContact: boolean;
  crossedPls: string[];
}

/** Everything an S-2 estimate may reason from, and nothing else. */
export interface EstimatorContext {
  tick: number;
  elapsedMin: number;
  phase: string;
  dials: Dials;
  graphics: Graphic[];
  objectiveId?: string;
  /** The area of operations, for a line of orientation in the state. */
  aoTitle?: string;
  contacts: CopContact[];
  own: CopOwnUnit[];
  frame: CorridorFrame;
}

/** Tracks, across a run, the first tick each enemy id was reported. The live stage keeps one of these
 * per fight; the calibration harness rebuilds one per sampled run. */
export type FirstSeenMap = Map<number, number>;

/** Folds a batch of contacts into a first-seen map, returning it for chaining. */
export function noteFirstSeen(map: FirstSeenMap, contacts: readonly Contact[], tick: number): FirstSeenMap {
  for (const c of contacts) {
    if (!map.has(c.enemyId)) map.set(c.enemyId, Math.min(tick, c.reportedAt));
  }
  return map;
}

export interface ContextOptions {
  dials: Dials;
  scenario?: Scenario | null;
  graphics?: Graphic[];
  aoTitle?: string;
  firstSeen?: FirstSeenMap;
}

/**
 * The single narrowing point: a full snapshot in, a picture-only context out.
 *
 * Units are filtered to side 0 *here*, not at the call site, and contacts are filtered to those whose
 * report has actually arrived. Passing a snapshot taken with `includeEnemy: true` — which the headless
 * harness does, because it needs the enemy positions for its labels — is therefore safe: the red units
 * are dropped on the way in and no downstream code ever had them.
 */
export function estimatorContextFromSnapshot(snap: Snapshot, opts: ContextOptions): EstimatorContext {
  const graphics = opts.graphics ?? opts.scenario?.graphics ?? [];
  const own: CopOwnUnit[] = [];
  for (const u of snap.units) {
    if (u.side !== 0) continue;
    own.push({
      id: u.id,
      callsign: u.callsign,
      type: u.type,
      x: u.x,
      y: u.y,
      strength: u.strength,
      alive: u.alive,
      inContact: u.inContact,
      crossedPls: [...u.crossedPls],
    });
  }

  const firstSeen = opts.firstSeen;
  const contacts: CopContact[] = [];
  for (const c of snap.picture.contacts) {
    if (c.reportedAt > snap.tick) continue;
    contacts.push({
      enemyId: c.enemyId,
      x: c.x,
      y: c.y,
      errorM: c.errorM,
      typeConf: c.typeConf,
      lastSeen: c.lastSeen,
      reportedAt: c.reportedAt,
      firstSeen: firstSeen?.get(c.enemyId) ?? c.reportedAt,
    });
  }
  contacts.sort((a, b) => a.enemyId - b.enemyId);

  const frame = corridorFrame(
    graphics,
    opts.scenario?.objectiveId,
    own.map((u) => ({ x: u.x, y: u.y })),
  );

  return {
    tick: snap.tick,
    elapsedMin: snap.tick / TICKS_PER_MIN,
    phase: snap.phase,
    dials: opts.dials,
    graphics,
    objectiveId: opts.scenario?.objectiveId,
    aoTitle: opts.aoTitle,
    contacts,
    own,
    frame,
  };
}

/** The same narrowing from the sim's own arrays, for the headless harness, which holds `Unit[]` and a
 * `Picture` rather than a `Snapshot`. Red units in `units` are dropped exactly as above. */
export function estimatorContextFromParts(args: {
  tick: number;
  phase: string;
  units: readonly Unit[];
  contacts: readonly Contact[];
  opts: ContextOptions;
}): EstimatorContext {
  const snap: Snapshot = {
    tick: args.tick,
    units: args.units.map((u) => ({ ...u, crossedPls: [...u.crossedPls] })),
    picture: { contacts: args.contacts.map((c) => ({ ...c })), reported: [] },
    orders: [],
    net: { occupancy: 0, queueSec: 0 },
    force: { strength: 0, fuel: 0, ammo: 0 },
    phase: args.phase,
    ended: null,
  };
  return estimatorContextFromSnapshot(snap, args.opts);
}

// ---- small shared readings off the context --------------------------------------------------------------

/** The maneuver platoons that count as combat power for an estimate. */
export const MANEUVER_TYPES: ReadonlySet<UnitType> = new Set<UnitType>(['armor', 'mech', 'inf']);

/** Contacts by third, in index order. Stale contacts count for less: a report ten minutes old is not
 * evidence of where the enemy is now, and the detection model drops it entirely at ten minutes. */
export function contactWeightByThird(ctx: EstimatorContext, halfLifeMin = 5): [number, number, number] {
  const sums: [number, number, number] = [0, 0, 0];
  for (const c of ctx.contacts) {
    const ageMin = Math.max(0, (ctx.tick - c.lastSeen) / TICKS_PER_MIN);
    const recency = Math.pow(0.5, ageMin / halfLifeMin);
    sums[thirdOf(ctx.frame, c)] += recency;
  }
  return sums;
}

/** Blue's own maneuver platoons by third — where the player has actually committed, which is the single
 * strongest predictor of where a doctrinal enemy will commit its reserve. */
export function ownManeuverByThird(ctx: EstimatorContext): [number, number, number] {
  const sums: [number, number, number] = [0, 0, 0];
  for (const u of ctx.own) {
    if (!u.alive || !MANEUVER_TYPES.has(u.type)) continue;
    sums[thirdOf(ctx.frame, u)] += 1;
  }
  return sums;
}

/** How many of blue's maneuver platoons are past a named band, by third. The scripted red cell commits
 * its reserve on exactly this count (two past PL WHITE in one third), so a heuristic that computes it
 * is not guessing — it is reading the same gauge red reads, through the fog of red's own detection. */
export function ownPastBandByThird(ctx: EstimatorContext, bandId: string): [number, number, number] {
  const sums: [number, number, number] = [0, 0, 0];
  for (const u of ctx.own) {
    if (!u.alive || !MANEUVER_TYPES.has(u.type)) continue;
    if (!isPastBand(ctx.frame, u, bandId)) continue;
    sums[thirdOf(ctx.frame, u)] += 1;
  }
  return sums;
}

/** The phase line the estimate hangs off: PL WHITE where there is one, else the deepest line there is. */
export function reserveTriggerBandId(frame: CorridorFrame): string | undefined {
  const pls = frame.bands.filter((b) => b.kind === 'pl');
  if (pls.length === 0) return undefined;
  const white = pls.find((b) => b.name.toUpperCase().includes('WHITE'));
  return (white ?? pls[Math.min(1, pls.length - 1)]).id;
}
